import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { surveyQuestions, categoryInfo, Question } from '../data/surveyQuestions';

interface SurveyResponse {
  questionId: string;
  value: number;
}

interface LikertScaleProps {
  questionId: string;
  value: number | null;
  onChange: (questionId: string, value: number) => void;
}

const LikertScale: React.FC<LikertScaleProps> = ({ questionId, value, onChange }) => {
  const options = [
    { value: 1, label: '1. Strongly Disagree' },
    { value: 2, label: '2. Disagree' },
    { value: 3, label: '3. Neutral' },
    { value: 4, label: '4. Agree' },
    { value: 5, label: '5. Strongly Agree' },
  ];

  return (
    <div className="space-y-3">
      {options.map((option) => (
        <label
          key={option.value}
          className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
        >
          <input
            type="radio"
            name={questionId}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(questionId, option.value)}
            className="w-4 h-4 text-blue-600 focus:ring-blue-500 focus:ring-2"
          />
          <span className="text-sm font-medium text-gray-700">{option.label}</span>
        </label>
      ))}
    </div>
  );
};

const Survey: React.FC = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, number>>({});
  const [currentSection, setCurrentSection] = useState<'empathy' | 'growth' | 'adaptability' | 'purpose'>('empathy');

  const currentQuestion = surveyQuestions[currentQuestionIndex];
  const totalQuestions = surveyQuestions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  // Get questions by section for navigation
  const questionsBySection = {
    empathy: surveyQuestions.filter(q => q.category === 'empathy'),
    growth: surveyQuestions.filter(q => q.category === 'growth'),
    adaptability: surveyQuestions.filter(q => q.category === 'adaptability'),
    purpose: surveyQuestions.filter(q => q.category === 'purpose'),
  };

  const handleResponseChange = (questionId: string, value: number) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      const nextQuestion = surveyQuestions[currentQuestionIndex + 1];
      if (nextQuestion.category !== currentSection) {
        setCurrentSection(nextQuestion.category);
      }
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      const prevQuestion = surveyQuestions[currentQuestionIndex - 1];
      if (prevQuestion.category !== currentSection) {
        setCurrentSection(prevQuestion.category);
      }
    }
  };

  const jumpToSection = (section: 'empathy' | 'growth' | 'adaptability' | 'purpose') => {
    const sectionStartIndex = surveyQuestions.findIndex(q => q.category === section);
    setCurrentQuestionIndex(sectionStartIndex);
    setCurrentSection(section);
  };

  const getSectionProgress = (section: 'empathy' | 'growth' | 'adaptability' | 'purpose') => {
    const sectionQuestions = questionsBySection[section];
    const answeredInSection = sectionQuestions.filter(q => responses[q.id] !== undefined).length;
    return (answeredInSection / sectionQuestions.length) * 100;
  };

  const getEstimatedTimeRemaining = () => {
    const unansweredQuestions = totalQuestions - Object.keys(responses).length;
    const timePerQuestion = 0.5; // 30 seconds per question
    return Math.max(1, Math.ceil(unansweredQuestions * timePerQuestion));
  };

  const canProceed = responses[currentQuestion.id] !== undefined;

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Sidebar - Progress */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Assessment Progress</h3>
              
              {/* Overall Progress */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>{currentQuestionIndex + 1} of {totalQuestions}</span>
                  <span>questions</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-green-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">{progress.toFixed(0)}% complete</p>
              </div>

              {/* Time Remaining */}
              <div className="mb-6 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  Estimated time remaining: <strong>{getEstimatedTimeRemaining()} minutes</strong>
                </p>
              </div>

              {/* Sections */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Sections</h4>
                <div className="space-y-3">
                  {Object.entries(categoryInfo).map(([key, info]) => {
                    const section = key as 'empathy' | 'growth' | 'adaptability' | 'purpose';
                    const sectionProgress = getSectionProgress(section);
                    const isCurrentSection = section === currentSection;
                    
                    return (
                      <button
                        key={section}
                        onClick={() => jumpToSection(section)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          isCurrentSection 
                            ? 'bg-blue-50 border border-blue-200' 
                            : 'hover:bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900">{info.name}</span>
                          {sectionProgress === 100 && (
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          )}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1 mb-1">
                          <div 
                            className="h-1 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${sectionProgress}%`,
                              backgroundColor: info.color
                            }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500">{info.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content - Question */}
          <div className="lg:col-span-2">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              {/* Question Header */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: categoryInfo[currentQuestion.category].color }}
                    ></div>
                    <span className="text-sm font-medium text-gray-600">
                      {categoryInfo[currentQuestion.category].name} • Likert Scale
                    </span>
                  </div>
                </div>
                
                <h1 className="text-xl font-semibold text-gray-900 leading-relaxed">
                  {currentQuestion.text}
                </h1>
              </div>

              {/* Likert Scale */}
              <div className="mb-8">
                <LikertScale
                  questionId={currentQuestion.id}
                  value={responses[currentQuestion.id] || null}
                  onChange={handleResponseChange}
                />
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                <button
                  onClick={goToPreviousQuestion}
                  disabled={currentQuestionIndex === 0}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span>Previous</span>
                </button>

                <div className="text-sm text-gray-500">
                  Question {currentQuestionIndex + 1} of {totalQuestions}
                </div>

                <button
                  onClick={goToNextQuestion}
                  disabled={!canProceed || currentQuestionIndex === totalQuestions - 1}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <span>Next Question</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Submit Section (shown on last question) */}
            {currentQuestionIndex === totalQuestions - 1 && canProceed && (
              <div className="mt-6 bg-green-50 p-6 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-green-900 mb-2">Ready to Submit?</h3>
                <p className="text-green-700 mb-4">
                  You've answered all questions. Click submit to generate your EGAP culture assessment results.
                </p>
                <button className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors">
                  Submit Assessment
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Survey;